import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { CheckCircleOutline as HaveIcon, RadioButtonUnchecked as GapIcon } from '@mui/icons-material';
import { jobsApi } from '../../services/api';
import { JobMatch } from '../../types';

interface Props {
  jobId: string;
  headingComponent: 'h2' | 'h3';
}

// A posting takes the model up to about half a minute to read.
const ASK_AGAIN_MS = 5000;
const MAX_ASKS = 10;

const Terms: React.FC<{ label: string; terms: JobMatch['have']; have: boolean }> = ({ label, terms, have }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    <Stack direction="row" component="ul" aria-label={label} sx={{ flexWrap: 'wrap', gap: 0.75, listStyle: 'none', p: 0, m: 0 }}>
      {terms.map((item) => (
        <Chip
          key={item.term}
          component="li"
          size="small"
          variant="outlined"
          color={have ? 'success' : 'default'}
          icon={have ? <HaveIcon /> : <GapIcon />}
          label={item.must ? `${item.term} · must-have` : item.term}
          sx={item.must && !have ? { fontWeight: 600 } : undefined}
        />
      ))}
    </Stack>
  </Box>
);

/**
 * How the signed-in person matches this job: of the skills and tools the
 * posting names, the ones their resume, Experience library and profile have,
 * and the ones they do not. Says nothing when it cannot be worked out.
 */
const JobMatchSection: React.FC<Props> = ({ jobId, headingComponent }) => {
  const [match, setMatch] = useState<JobMatch | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed' | 'plan'>('loading');

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let asked = 0;
    setState('loading');
    setMatch(null);
    const ask = () => {
      asked += 1;
      jobsApi
        .getMatch(jobId, controller.signal)
        .then((data) => {
          setMatch(data);
          setState('ready');
          // The posting is being read in the background: what came back is from its tags. Ask again shortly, for a while.
          if (data.reading && asked < MAX_ASKS) timer = setTimeout(ask, ASK_AGAIN_MS);
        })
        .catch((e) => {
          if (controller.signal.aborted) return;
          setState((e as { response?: { status?: number } })?.response?.status === 403 ? 'plan' : 'failed');
        });
    };
    ask();
    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [jobId]);

  const nothingWritten = match !== null && !match.sources.resume && !match.sources.library && !match.sources.profile;
  const from = match ? ['your resume', match.sources.library ? 'your Experience library' : '', match.sources.profile ? 'your profile' : ''].filter(Boolean) : [];
  const where = from.length > 1 ? `${from.slice(0, -1).join(', ')} and ${from[from.length - 1]}` : from[0];

  return (
    <Box component="section" aria-labelledby={`match-${jobId}`} sx={{ mb: 2.5 }}>
      <Typography id={`match-${jobId}`} variant="subtitle1" component={headingComponent} sx={{ fontWeight: 600, mb: 1 }}>
        How you match
      </Typography>
      {state === 'plan' && (
        <Typography variant="body2" color="text.secondary">
          See which of the skills and tools this job names you have, and which are missing, with{' '}
          <Link component={RouterLink} to="/plan">
            JobFlo Pro
          </Link>
          .
        </Typography>
      )}
      {state === 'failed' && (
        <Typography variant="body2" color="text.secondary">
          The match could not be worked out just now. Reload the page to try again.
        </Typography>
      )}
      {(state === 'loading' || (state === 'ready' && match?.reading && match.counted === 0 && !nothingWritten)) && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} aria-hidden="true" />
          <Typography variant="body2" color="text.secondary" role="status">
            Reading what this posting asks for...
          </Typography>
        </Stack>
      )}
      {match && state === 'ready' && nothingWritten && (
        <Typography variant="body2" color="text.secondary">
          <Link component={RouterLink} to="/resumes">
            Upload a resume
          </Link>{' '}
          to see which of this job&apos;s {match.counted > 0 ? `${match.counted} named skills and tools` : 'skills and tools'} you have.
        </Typography>
      )}
      {match && state === 'ready' && !nothingWritten && match.counted === 0 && !match.reading && (
        <Typography variant="body2" color="text.secondary">
          This posting names no specific skills or tools to compare with your resume.
        </Typography>
      )}
      {match && state === 'ready' && match.score !== null && (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="baseline">
            <Typography variant="h4" component="p" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {match.score}%
            </Typography>
            <Typography variant="body2">
              {match.have.length} of the {match.counted} skills and tools this posting names are in {where}.
            </Typography>
          </Stack>
          {match.have.length > 0 && <Terms label="You have" terms={match.have} have />}
          {match.gaps.length > 0 && <Terms label="Not found" terms={match.gaps} have={false} />}
          {match.gaps.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Have one of these but never wrote it down?{' '}
              <Link component={RouterLink} to="/resumes#library">
                Add what you did with it to your Experience library
              </Link>
              : it counts here, and a resume tailored for this job can use it.
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            A count of named skills and tools, with must-haves counting double. Years of experience and the like are not
            judged.
            {match.reading
              ? ' Reading the full posting now: this goes by its technology tags until that is done, in a few seconds.'
              : match.read === 'tags'
                ? ' The posting could not be read just now, so this goes by its technology tags.'
                : ''}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default JobMatchSection;
